<script>
import { ref }     from "vue";
import PageHeader  from "@/components/page-header";
import Review      from "@/components/review.vue";
import ReviewReply from "@/components/review-reply";
import axios       from "axios";
import Utils       from "@/js/utils";
import sources     from "@/data/sources";
import DocsButton  from "@/components/documentation-button";

export default {
  data() {
    return {
      loading: false,
      company_id: null,
      review_id: null,
      translations: { [this.$i18n.locale]: {} },
      replyPermission: false,
      cache: { review: [] },
      sources: {},
      user: {},
      additionalInfo: [
        { field: "reviewer_name", i18nPath: "name" },
        { field: "travel_composition_name", i18nPath: "travel_composition" },
        { field: "creation_date", i18nPath: "date" },
        { field: "package", i18nPath: "package" },
        { field: "helpful_votes", i18nPath: "helpful_votes" },
        { field: "price", i18nPath: "price" },
        { field: "room_name", i18nPath: "room_name" },
        { field: "reviewer_country", i18nPath: "country" },
        { field: "source_name", i18nPath: "source" },
        { field: "language_name_translated", i18nPath: "language" },
      ],
      modals: ref({ reply: { review: false } }),
      generateResponse: false,
      responseSettings: {
        tonality: "professional",
        customTonality: "",
        language: {
          use_review_language: false,
          customLanguage: this.$i18n.locale
        },
        mention_property_name: {
          enabled: true,
          value: null,
        },
        sign_name: {
          enabled: true,
          value: null,
        },
        limit_words: {
          enabled: true,
          value: null,
        },
        words_to_mention: {
          enabled: true,
          selected: [],
          options: []
        },
        saved_responses: {
          value: null,
          options: []
        }
      },
    };
  },
  components: { PageHeader, Review, ReviewReply, DocsButton },
  provide() {
    return {
      modals:            this.modals,
      setSelectedItemId: () => {}
    };
  },
  async created() {
    this.sources = sources;
    this.user = await this.$store.dispatch("user/fetch");
    this.company_id = this.$route.query.cid || this.$route.query.company_id;
    this.review_id  = this.$route.query.rid || this.$route.query.review_id;
    if (this.company_id && this.review_id) this.loadReview();
  },
  methods: {
    async loadReview() {
      Utils.setLoading.bind(this)(true);
      this.cache.review = [];

      let response = await axios.get(`/v3/companies/${this.company_id}/reviews/${this.review_id}`);
      response = response.data.data;
      ["translated"].forEach(field => {
        response[field] = false;
        response.source_name = this.sources[response.source].friendly_name;
      });

      this.cache.review = [response];
      await this.loadReplyPermission();

      Utils.setLoading.bind(this)(false);
    },
    async loadReplyPermission() {
      if (Utils.permittedReviewSources.includes(this.review?.source)) {
        let response = await axios.get(`/v3/oauth/credentials/${this.company_id}/active`);
        this.replyPermission = response.data.data?.length > 0;
      }
    },
    goToFeed() {
      this.$router.push({ name: "reputation_reviews" });
    },
    closeReplyModal() {
      this.modals.reply.review = false;
      this.generateResponse = false;
    }
  },
  computed: {
    review() {
      return this.cache.review[0];
    },
    comments() {
      return this.review.comments.reduce((res, c) => {
        if (this.review.translated && this.translations[this.$i18n.locale]?.[`review_${this.review.id}`]?.[`comment_${c.kind}`])
          res[c.kind] = this.translations[this.$i18n.locale][`review_${this.review.id}`][`comment_${c.kind}`];
        else
          res[c.kind] = c.comment;
        return res;
      }, {});
    },
    openOffcanvas() {
      return this.$store.getters["documentation/documentationRender"]?.["management_responses"]?.open;
    },
    insideExtension() {
      return this.$store.getters["extension/insideExtension"];
    }
  }
}
</script>

<template>
  <div class="w-100" :class="{ 'cursor-loading': loading }">
    <PageHeader :title="$t('daily_operations.reviews.review')" />
    <div class="row">
      <div class="col-lg-12">
        <!-- Time line -->
        <div class="card">
          <div class="card-body mt-5 mb-5">
            <div class="row justify-content-center">
              <div class="col-lg-10 d-flex flex-column">
                <template v-if="cache.review[0] && !loading">
                  <ul class="verti-timeline list-unstyled border-0" dir="ltr" v-if="!loading">
                    <Review :review="review" :reviewIndex="0" :cache="cache" :params="{ page: 'review' }" :translations="translations" :permittedReply="replyPermission" :sources="sources" :user="user" :comments="comments" :additionalInfo="additionalInfo" />
                  </ul>
                  <button @click="goToFeed" class="btn btn-primary mt-5 align-self-center">
                    {{ $t("daily_operations.reviews.go_to_feed") }}
                  </button>
                </template>

                <div style='min-height: 200px;' class="loading" v-if="loading"></div>

                <div class="d-flex align-items-center justify-content-center" v-if="!loading && !cache.review[0]">
                  <span class="font-size-16">{{ $t("general.no_data") }}</span>
                </div>

                <!-- review reply modal -->
                <b-modal v-model="modals.reply.review" class="review-modal" centered header-class="border-0 pb-0" footer-class="border-0 pt-2 p-3" body-class="pt-1" :class="{ 'cursor-loading': loading, 'modal-offcanvas': openOffcanvas }" size="xl" @cancel:modelValue="closeReplyModal" @hide="closeReplyModal" scrollable :hide-footer="true">
                  <template #header>
                    <div class="d-flex align-items-center justify-content-between w-100">
                      <h5 class="border-0 pb-0 modal-title">{{ $t("daily_operations.reviews.reply_review") }}</h5>
                      <div class="d-flex align-items-center">
                        <DocsButton component="management_responses" :tooltip="$t('general.help')" customClass="mdi mdi-help-circle font-size-20" />
                        <button class="btn-close" type="button" @click="closeReplyModal"></button>
                      </div>
                    </div>
                  </template>
                  <ReviewReply v-if="review && review.id" :review="review" :permittedReply="replyPermission" :generate="generateResponse" :responseSettings="responseSettings" :comments="comments" :additionalInfo="additionalInfo" :insideExtension="insideExtension" />
                </b-modal>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
